import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Skeleton } from "@mui/material";

import { useProfileData } from "../../../../hooks/useProfileData";
import { getGinoChatRoute } from "../../../../routers/routeUtils";

import GinoHeader from "../../components/GinoHeader";
import GinoChatInput from "../../components/GinoChatInput";
import WelcomeScreen from "../../components/WelcomeScreen";
import { useGetGinoCredits } from "../../api/queries/ginoCredits";
import {
  ScoutPlayerIcon,
  HireProfessionalIcon,
  TrainingPlanIcon,
  NutritionPlanIcon,
  TrialPrepTryoutIcon,
} from "../../assets/icons/gino-icons";

const QUICK_ACTIONS = [
  { labelKey: "quickActionFindEvents", promptKey: "quickActionFindEventsPrompt" },
  { labelKey: "quickActionFindPlayers", promptKey: "quickActionFindPlayersPrompt" },
  { labelKey: "quickActionFindCoaches", promptKey: "quickActionFindCoachesPrompt" },
  { labelKey: "quickActionFindPrograms", promptKey: "quickActionFindProgramsPrompt" },
  { labelKey: "quickActionFindJobs", promptKey: "quickActionFindJobsPrompt" },
  { labelKey: "quickActionTrainingPlan", promptKey: "quickActionTrainingPlanPrompt" },
  { labelKey: "quickActionTalentReport", promptKey: "quickActionTalentReportPrompt" },
  { labelKey: "quickActionBuildCampaign", promptKey: "quickActionBuildCampaignPrompt" },
];

const GinoHomePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("gino", { keyPrefix: "homePage" });
  const { data: ginoCredits, isLoading: isLoadingGinoCredits, error: errorGinoCredits } = useGetGinoCredits();
  const { data: profileData } = useProfileData();

  const [openCreditLeftModal, setOpenCreditLeftModal] = useState(false);
  const handleOpenCreditLeftModal = () => setOpenCreditLeftModal(true);
  const handleCloseCreditLeftModal = () => setOpenCreditLeftModal(false);

  const isCreditsLow = ginoCredits?.balance !== undefined && ginoCredits.balance <= 5;

  const handleSend = (message: string) => {
    if (isCreditsLow) {
      handleOpenCreditLeftModal();
      return;
    }
    navigate(getGinoChatRoute(), { state: { message } });
  };

  const handleQuickAction = (prompt: string) => {
    if (isCreditsLow) {
      handleOpenCreditLeftModal();
      return;
    }
    navigate(getGinoChatRoute(), { state: { message: prompt } });
  };

  return (
    <Box sx={styles.container}>
      <GinoHeader
        ginoCredits={ginoCredits}
        isLoadingGinoCredits={isLoadingGinoCredits}
        errorGinoCredits={errorGinoCredits}
        openCreditLeftModal={openCreditLeftModal}
        handleOpenCreditLeftModal={handleOpenCreditLeftModal}
        handleCloseCreditLeftModal={handleCloseCreditLeftModal}
      />
      <Box sx={styles.mainContainer}>
        <WelcomeScreen userName={profileData?.fullName?.split(" ")[0]} userRole={profileData?.role} />
        <GinoChatInput disabled={isLoadingGinoCredits} onSend={handleSend} />
        <Box sx={styles.quickActionsContainer}>
          {QUICK_ACTIONS.map((action) => (
            <Button key={action.promptKey} sx={styles.quickAction} onClick={() => handleQuickAction(t(action.promptKey))}>
              {t(action.labelKey)}
            </Button>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default GinoHomePage;

const styles = {
  container: {
    width: "100%",
    background: "var(--background-surface-default, #FFF)",
    padding: { xs: "0", md: "32px" },
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  mainContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: { xs: "20px 0", sm: "40px 30px", md: "40px 80px" },
  },
  quickActionsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    justifyContent: "center",
    maxWidth: "720px",
    margin: "0 auto",
  },
  quickAction: {
    padding: "10px 18px",
    background: "#fff !important",
    border: "1.5px solid #e5e7eb",
    borderRadius: "9999px",
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
    fontFamily: "Montserrat",
    textTransform: "none",
    whiteSpace: "nowrap",
    "&:hover": { borderColor: "#106048", color: "#106048", background: "#f0fdf4 !important" },
  },
};
